import React, { useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authFetch, setApiToken, refreshCsrfAfterAuth } from './authFetch';
import { UserContext } from './App';
import Debug from './Debug';

// ===== DEBUG MODE TOGGLE =====
// Set to true to enable debug UI for troubleshooting
// Set to false for production (default)
const DEBUG_MODE = false;
// =============================

// Production mode check - disable verbose logging in production
const IS_DEV = process.env.NODE_ENV === 'development';
const devLog = (message) => { if (IS_DEV) console.log(`[OAuth] ${message}`); };

function OAuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrentUser } = useContext(UserContext) || {};
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const handleOAuthSuccess = async () => {
      try {
        devLog('OAuth callback processing');

        const urlParams = new URLSearchParams(location.search);
        const token = urlParams.get('token');

        if (!token) {
          devLog('No OAuth token found');
          if (DEBUG_MODE) {
            return;
          }
          navigate('/login?error=no_token');
          return;
        }

        devLog('Exchanging OAuth token for session');

        const exchangeResponse = await authFetch(`${BASE_URL}/api/oauth/exchange/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ token })
        });

        if (exchangeResponse.ok) {
          const responseData = await exchangeResponse.json();

          if (responseData.success && responseData.user) {
            const userData = responseData.user;

            devLog('OAuth token exchange successful');

            if (userData.api_token) {
              setApiToken(userData.api_token);
              devLog('API token stored');
            }

            // Refresh CSRF token immediately after successful OAuth login
            devLog('Refreshing CSRF token');
            await refreshCsrfAfterAuth();

            if (setCurrentUser) {
              setCurrentUser(userData);
            }

            localStorage.setItem('userDetails', JSON.stringify(userData));

            navigate('/');
          } else {
            devLog('Token exchange failed');
            if (DEBUG_MODE) {
              return;
            }
            navigate('/login?error=exchange_failed');
          }
        } else {
          devLog('Token exchange request failed');
          if (DEBUG_MODE) {
            return;
          }
          navigate('/login?error=exchange_request_failed');
        }
      } catch (error) {
        devLog('Error handling OAuth callback');
        if (DEBUG_MODE) {
          return;
        }
        navigate('/login?error=callback_error');
      }
    };

    handleOAuthSuccess();
  }, [navigate, setCurrentUser, BASE_URL, location.search]);

  // Only show debug UI if DEBUG_MODE is enabled
  if (DEBUG_MODE) {
    return (
      <div className="max-w-4xl mx-auto mt-8 p-8">
        <div className="bg-yellow-50 border border-yellow-300 rounded p-4 mb-4">
          <h2 className="text-xl font-bold text-yellow-800 mb-2">🔧 Debug Mode Enabled</h2>
          <p className="text-sm text-gray-700">
            Debug mode is active. Use the tools below to troubleshoot authentication issues.
            Check the browser console (F12) for detailed logs.
          </p>
        </div>
        
        <Debug />
        
        <div className="mt-4 text-center">
          <button 
            onClick={() => navigate('/login')}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // Normal loading screen for users
  return (
    <div className="max-w-md mx-auto mt-12 p-8 bg-white rounded shadow text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <h2 className="text-xl font-semibold mb-2">Completing Sign In</h2>
      <p className="text-gray-600">Please wait while we establish your session...</p>
    </div>
  );
}

export default OAuthCallback;
